/**
 * `mobile` 네임스페이스 — 한국어. `en/mobile.ts`의 shape를 그대로 따른다
 * (키 누락은 타입 에러). 용어는 기존 web 네임스페이스(settings/orders/approvals/
 * backtest/strategiesDetail)의 glossary를 그대로 따른다 — 모의 실행, 매수/매도,
 * 시장가/지정가, 잠금 해제됨/잠김, {qty}주, CAGR 등. 서버 enum/상태 토큰(실행
 * 상태, 주문 상태, 결정 단계)은 번역하지 않는다 — 웹 콘솔과 동일하게 뱃지로
 * 그대로 표시된다.
 */
import type { MobileKey } from "../en/mobile";

export const mobile: Record<MobileKey, string> = {
  appName: "richrich",

  // ── shared across screens ──
  errorCouldNotLoadTitle: "불러오지 못했습니다",
  loadingPortfolio: "포트폴리오 불러오는 중…",
  asOfLabel: "{date} 기준",
  portfolioValueLabel: "포트폴리오 가치 · {currency}",
  statsStocks: "주식",
  statsCash: "현금",
  portfolioNoSnapshotTitle: "아직 스냅샷 없음",
  portfolioNoSnapshotMessage: "첫 스냅샷이 생성되면 포트폴리오 가치가 표시됩니다.",
  dryRunLabel: "모의 실행",
  liveLabel: "실거래",
  updatedRelative: "{when} 업데이트",
  errorNotFound: "찾을 수 없음",
  winRateLabel: "승률",
  payoffRatioLabel: "손익비",
  tradesLabel: "거래",
  marketLabel: "시장가",
  linkedLabel: "연결됨",
  qtySuffixSh: "{qty}주",

  // ── navigation: tab titles + stack titles ──
  tabDashboard: "대시보드",
  tabStrategies: "전략",
  tabOrders: "주문",
  tabApprovals: "승인",
  tabAlerts: "알림",
  settingsLabel: "설정",
  builderChatLabel: "빌더 챗",
  strategyDetailTitle: "전략",
  decisionReplayTitle: "결정 리플레이",
  backtestsLabel: "백테스트",
  backtestResultTitle: "백테스트 결과",
  portfolioTitle: "포트폴리오",
  approvalTitle: "승인",

  // ── login ──
  loginConnecting: "연결 중…",
  loginClaimHeading: "이 인스턴스 소유권 등록",
  loginSignInHeading: "인스턴스에 로그인",
  loginEmailLabel: "이메일",
  loginEmailPlaceholder: "you@example.com",
  loginClaimHint: "처음 인증하는 이메일이 유일한 소유자가 됩니다.",
  loginCodeHint: "소유자 이메일로 일회용 코드가 전송됩니다.",
  loginSending: "전송 중…",
  loginSendCode: "코드 전송",
  loginCodeLabel: "인증 코드",
  loginCodePlaceholder: "123456",
  loginCodeSentHint: "{email}(으)로 전송했습니다. 코드는 10분 후 만료됩니다.",
  loginVerifying: "확인 중…",
  loginClaimContinue: "등록 후 계속",
  loginSignIn: "로그인",
  loginUseDifferentEmail: "다른 이메일 사용",
  loginQrTip: "팁: 코드 입력 없이 로그인하려면 웹 앱의 설정에 있는 QR 코드를 카메라로 스캔하세요.",

  // ── QR 로그인 ──
  qrHeading: "QR 로그인",
  qrMissingTokenError: "이 QR 링크에 로그인 토큰이 없습니다.",
  qrErrorTitle: "이 QR로 로그인하지 못했습니다",
  qrExpiredHint:
    "QR 코드는 1회용이며 2분 후 만료됩니다. 웹 앱의 설정에서 새 코드를 생성해 다시 스캔해 주세요.",
  qrBackToSignIn: "로그인으로 돌아가기",
  qrSigningIn: "로그인 중…",

  // ── 대시보드 ──
  dashboardConnectTossTitle: "포트폴리오를 보려면 Toss를 연결하세요",
  dashboardConnectTossDesc:
    "설정에서 Toss 자격 증명을 추가하고 인증하면 계좌 가치와 보유 종목을 확인할 수 있습니다.",
  dashboardOpenSettings: "설정 열기",
  dashboardProfitVsDeposit30d: "손익 대 입출금 · 30일",
  externalFlowLabel: "입출금",
  marketPnlLabel: "시장 손익",
  timeWeightedReturnLabel: "시간가중수익률",
  decompEndValueLabel: "기말 평가액",
  decompNetDepositsLabel: "순입출금",
  decompTwrLabel: "TWR",
  decompCapitalLegend: "원금 (입금)",
  decompGainLegend: "시장 이익",
  decompLossLegend: "시장 손실",
  decompValueLegend: "평가액",

  // ── 설정 ──
  settingsLoading: "설정 불러오는 중…",
  settingsAccountHeader: "계정",
  settingsSignedInAs: "로그인 계정",
  settingsSignOut: "로그아웃",
  settingsPushHeader: "푸시 알림",
  settingsPushFeatureLabel: "푸시 기능",
  gateUnlocked: "잠금 해제됨",
  gateLocked: "잠김",
  settingsPushDesc:
    "이 기기를 등록하면 승인이 필요한 전략을 사용할 수 있습니다. EAS 자격 증명이 필요합니다: Android FCM V1과 iOS APNs + 유료 Apple Developer 계정 — 이 과정은 생략할 수 없습니다.",
  settingsPushProjectIdWarning: "등록하기 전에 app.json에 expo.extra.eas.projectId를 설정하세요.",
  settingsRegistering: "등록 중…",
  settingsRegisterDevice: "이 기기 등록",
  settingsPushRegistered: "이 기기가 푸시 알림에 등록되었습니다.",
  settingsTradingStateHeader: "거래 상태",
  settingsGlobalTrading: "전체 거래",
  tradingArmed: "실거래 활성",
  tradingDisarmed: "실거래 비활성",
  settingsDefaultDryRun: "기본 모의 실행",
  onLabel: "켜짐",
  offLabel: "꺼짐",
  settingsBaseCurrency: "기준 통화",
  settingsArmHint:
    "실거래 활성화는 웹 앱에서만 가능합니다 — 명시적 확인과 인증된 Toss 자격 증명이 필요합니다.",
  settingsFeaturesHeader: "기능",
  settingsFeatureAi: "AI 어드바이저 / 빌더",
  settingsFeatureToss: "Toss 거래",
  settingsFeaturePush: "푸시",
  settingsAppHeader: "앱",
  settingsApiOrigin: "API 주소",

  // ── 전략 목록 ──
  strategiesLoading: "전략 불러오는 중…",
  strategyNextRun: "다음 실행 {when}",
  strategiesEmptyTitle: "전략 없음",
  strategiesEmptyAllMessage: "웹 앱에서 전략을 만들면 여기에 표시됩니다.",
  strategiesEmptyFilteredMessage: "{filter} 상태의 전략이 없습니다.",

  // ── 전략 상세 ──
  strategyDetailLoading: "전략 불러오는 중…",
  strategySlug: "슬러그",
  strategySchema: "스키마",
  strategyAccount: "계좌",
  notBound: "연결 안 됨",
  strategyBudget: "예산",
  nextRunLabel: "다음 실행",
  updatedLabel: "업데이트",
  controlsHeader: "제어",
  dryRunControlDesc: "체결을 시뮬레이션합니다 — 켜져 있는 동안은 실제 주문이 나가지 않습니다.",
  pauseButton: "일시정지",
  resumeButton: "재개",
  pauseResumeUnavailable: "{status} 상태의 전략은 모바일에서 일시정지하거나 재개할 수 없습니다.",
  strategyRulesHeader: "전략 규칙",
  performanceHeader: "성과",
  realizedPnlLabel: "실현 손익",
  recentRunsHeader: "최근 실행",
  noRunsYet: "아직 실행 기록이 없습니다.",
  dryRunTag: "모의 실행",

  // ── 주문 ──
  ordersFilterAll: "전체",
  ordersFilterOpen: "진행 중",
  ordersFilterClosed: "종료",
  ordersFilledLabel: "체결 {qty}",
  whyLabel: "이유?",
  cancellingLabel: "취소 중…",
  cancelLabel: "취소",
  ordersLoading: "주문 불러오는 중…",
  cancelFailedTitle: "취소 실패",
  cancelOrderConfirmTitle: "이 주문을 취소할까요?",
  cancelDryRunNote: "(모의 실행 — 시뮬레이션)",
  keepOrderLabel: "주문 유지",
  cancelOrderLabel: "주문 취소",
  ordersEmptyTitle: "주문 없음",
  ordersEmptyAllMessage: "전략이 주문을 제출하면 여기에 표시됩니다.",
  ordersEmptyOpenMessage: "진행 중인 주문이 없습니다.",
  ordersEmptyClosedMessage: "종료된 주문이 없습니다.",

  // ── 승인 목록 ──
  approvalsLoading: "승인 불러오는 중…",
  approvalsEmptyTitle: "대기 중인 승인 없음",
  approvalsEmptyMessage: "승인이 필요한 전략의 주문 승인 요청이 여기에 표시됩니다.",
  pendingLabel: "대기 중",
  approvalsHighValueLabel: "고액",
  expiresLabel: "{when} 만료",

  // ── 승인 상세 ──
  approvalDetailLoading: "승인 불러오는 중…",
  approvalNoLongerPendingTitle: "더 이상 대기 중이 아님",
  approvalNoLongerPendingMessage:
    "이 승인은 이미 결정되었거나 만료되었습니다. 서버 측 타임아웃이 우선합니다.",
  approvalMissingTokenError: "승인 토큰이 없습니다.",
  qtySharesLabel: "{qty}주",
  approvalHighValueOrder: "고액 주문",
  sideLabel: "매매",
  orderTypeLabel: "주문 유형",
  sizeLabel: "수량",
  priceLabel: "가격",
  currencyLabel: "통화",
  timeInForceLabel: "주문 유효기간",
  approvalWindowHeader: "승인 기한",
  expiresFieldLabel: "만료",
  countdownLabel: "남은 시간",
  onTimeoutLabel: "타임아웃 시",
  requestedLabel: "요청 시각",
  approveOrderButton: "주문 승인",
  approvalTokenRequiredNote:
    "승인하려면 푸시 알림의 일회용 토큰이 필요합니다. 이 승인을 푸시에서 열어야 승인할 수 있습니다 — 거부는 여기서도 가능합니다.",
  rejectOrderButton: "주문 거부",
  approvalDecidedLabel: "{when} 결정됨",
  approvalDecidedViaSuffix: " · {via} 경유",

  // ── 백테스트 (설정 + 목록) ──
  backtestsGateTitle: "백테스트에는 시세 데이터가 필요합니다",
  backtestsGateDesc:
    "웹 앱의 설정에서 Toss 자격 증명을 연결하고 인증하면 백테스트용 과거 시세를 사용할 수 있습니다.",
  newBacktestHeader: "새 백테스트",
  fromLabel: "시작일",
  toLabel: "종료일",
  dateFormatPlaceholder: "YYYY-MM-DD",
  initialCashLabel: "초기 자본",
  queueingLabel: "대기열 등록 중…",
  runBacktestButton: "백테스트 실행",
  recentBacktestsHeader: "최근 백테스트",
  backtestsEmptyTitle: "아직 백테스트 없음",
  backtestsEmptyMessage: "위 양식으로 백테스트를 실행해 보세요.",

  // ── 백테스트 결과 ──
  backtestDetailLoading: "백테스트 불러오는 중…",
  backtestRunningNote: "캐시된 시세로 재생하는 중입니다 — 자동으로 새로고침됩니다.",
  equityCurveHeader: "자산 곡선",
  startingCapitalLabel: "시작금액",
  finalEquityLabel: "최종 평가금액",
  strategyLegendLabel: "전략",
  benchmarkLegendLabel: "벤치마크",
  equityExcessLabel: "벤치마크 대비 {value}",
  metricsHeader: "지표",
  totalReturnLabel: "총수익률",
  cagrLabel: "CAGR",
  maxDrawdownLabel: "최대 낙폭",
  benchmarkReturnLabel: "벤치마크 수익률",
  benchmarkMddLabel: "벤치마크 MDD",
  tradesCountValue: "{count}건 (왕복 {roundTrips}회)",
  backtestPriceRebasedNote:
    "각 선은 첫날을 100으로 환산해, 가격대가 다른 자산도 하나의 % 축에서 비교됩니다. RSI·ROC·낙폭과 FGI는 아래 보조 차트에 표시됩니다.",
  backtestPriceIndicatorNote: "지표는 표시된 구간에서 계산되어 구간 시작 부근의 값은 근사치입니다.",
  backtestPriceExpandLabel: "차트 크게 보기",

  // ── 빌더 챗: 세션 목록 ──
  chatUnlockAiTitle: "AI 빌더 사용하기",
  chatUnlockAiDesc:
    "웹 앱의 설정에서 AI 제공자 키를 추가하고 인증하면 전략 빌더 챗을 사용할 수 있습니다.",
  chatSessionsLoading: "세션 불러오는 중…",
  deleteFailedTitle: "삭제 실패",
  chatDeleteSessionTitle: "이 세션을 삭제할까요?",
  chatDeleteMessage: '"{title}"과(와) 메시지 기록이 삭제됩니다.',
  untitledSessionLabel: "제목 없는 세션",
  keepLabel: "유지",
  deleteLabel: "삭제",
  creatingLabel: "생성 중…",
  newSessionButton: "새 세션",
  chatSessionsEmptyTitle: "아직 세션이 없습니다",
  chatSessionsEmptyMessage: "세션을 시작하고 만들고 싶은 전략을 설명해 주세요.",

  // ── 빌더 챗: 대화 ──
  toolFailedLabel: "실패",
  chatConversationLoading: "대화 불러오는 중…",
  chatDescribeStrategy: "만들고 싶은 전략을 설명해 주세요.",
  chatComposerPlaceholder: "빌더에게 메시지…",
  stopLabel: "중지",
  sendLabel: "전송",
  jumpToLatestLabel: "최신으로 이동",
  savingLabel: "저장 중…",
  saveAsStrategyButton: "전략으로 저장",
  draftStrategyLabel: "초안 전략",
  turnRunningLabel: "서버에서 아직 처리 중인 턴이 있습니다",
  strategySavedTitle: "전략 저장됨",
  strategySavedMessage: '"{name}"이(가) 초안 전략으로 생성되었습니다.',
  stayHereLabel: "여기 머무르기",
  openStrategyLabel: "전략 열기",

  // ── 알림 ──
  notificationsLoading: "알림 불러오는 중…",
  markAllReadButton: "모두 읽음으로 표시",
  notificationsEmptyTitle: "알림 없음",
  notificationsEmptyMessage: "시그널, 체결, 승인, 리스크 이벤트가 여기에 표시됩니다.",

  // ── 포트폴리오 드릴다운 ──
  notEnoughSnapshotsMessage: "이 기간에는 스냅샷이 충분하지 않습니다.",
  allocationHeader: "자산 배분",
  noHoldingsMessage: "아직 보유 종목이 없습니다.",
  cashSliceName: "현금",
  allocationTotalLabel: "총 자산",
  cashflowsHeader: "입출금 내역",
  cashflowsEmptyMessage: "입금, 출금, 배당, 수수료가 여기에 표시됩니다.",
  breakdownHeader: "세부 내역",

  // ── 결정 리플레이 (실행 상세) ──
  indicatorsHeader: "지표",
  conditionsHeader: "조건",
  conditionTrueLabel: "참",
  conditionFalseLabel: "거짓",
  riskChecksHeader: "리스크 검사",
  riskPassLabel: "통과",
  riskFailLabel: "실패",
  aiVerdictHeader: "AI 판정",
  sizeMultiplierLabel: "크기 ×{multiplier}",
  orderIntentHeader: "주문 의도",
  typeLabel: "유형",
  marketLowercase: "시장가",
  runDetailLoading: "결정 리플레이 불러오는 중…",
  noDecisionsMessage: "이 실행에 기록된 결정이 없습니다.",
  scheduledLabel: "예약 시각",
  startedLabel: "시작 시각",
  finishedLabel: "종료 시각",
  tickLabel: "틱",

  // ── market overview (dashboard card + Market screen) ──
  marketScreenTitle: "시장",
  marketSectionTitle: "시장",
  marketUnavailable: "시장 데이터를 불러올 수 없습니다",
  marketLoadingLabel: "시장 정보 불러오는 중…",
  marketIndicesHeader: "지수",
  marketFgiHeader: "공포·탐욕 지수",
  marketSectorsHeader: "섹터",
  marketHeatmapHeader: "히트맵",
  marketNewsHeader: "뉴스",
  marketIndicesEmpty: "지수 데이터가 없습니다.",
  marketSectorsEmpty: "섹터 데이터가 없습니다.",
  marketNewsEmpty: "헤드라인이 없습니다.",
  // 종목 히트맵 — 히트맵 섹션의 "종목" 토글 뷰
  heatmapToggleSectors: "섹터",
  heatmapToggleStocks: "종목",
  marketStocksEmpty: "종목 데이터가 없습니다.",
  marketLegendNoData: "데이터 없음",
  marketStocksAllSectors: "‹ 전체",
  marketStocksBaselinePending: "등락률은 다음 장 마감 이후 표시됩니다.",
  marketStocksLockedTitle: "종목 히트맵은 Toss 연결이 필요합니다",
  marketStocksLockedDesc:
    "설정에서 Toss 자격 증명을 추가하고 인증하면 종목별 히트맵을 볼 수 있습니다.",
  marketFgiEmpty: "공포·탐욕 지수를 불러올 수 없습니다.",
  marketFgiHistoryEmpty: "추이 데이터가 없습니다.",
  fgiScoreLabel: "점수",
  fgiHistoryLabel: "90일 추이",
  fgiExtremeFear: "극도의 공포",
  fgiFear: "공포",
  fgiNeutral: "중립",
  fgiGreed: "탐욕",
  fgiExtremeGreed: "극도의 탐욕",
  fgiPrevClose: "전일",
  fgiPrev1Week: "1주 전",
  fgiPrev1Month: "1개월 전",
  fgiPrev1Year: "1년 전",
  // index short labels (keyed by Yahoo symbol)
  idxGSPC: "S&P 500",
  idxIXIC: "나스닥",
  idxDJI: "다우",
  idxRUT: "러셀",
  idxVIX: "VIX",
  // sector short labels (keyed by SPDR ETF symbol)
  secXLK: "기술",
  secXLF: "금융",
  secXLE: "에너지",
  secXLV: "헬스케어",
  secXLI: "산업재",
  secXLY: "임의소비재",
  secXLP: "필수소비재",
  secXLU: "유틸리티",
  secXLB: "소재",
  secXLRE: "부동산",
  secXLC: "커뮤니케이션",
};
